import EmailViewSummary from "@/domain/entities/dashboard/EmailViewSummary";
import { eventKeys } from "@/lib/eventKeys";
import { queryKeys } from "@/lib/queryKeys";
import emailViewSummaryMapper from "@/mappers/emailViewSummaryMapper";
import { useSdk, ViewSummary } from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import useGetEventBus from "./useGetEventBus";
import useFetchErrorNotifier from "./utils/useFetchErrorNotifier";

const useGetDashboardData = () => {
    const { mailManager } = useSdk();
    const queryClient = useQueryClient();
    const notifyFetchError = useFetchErrorNotifier();

    const fetchDashboardData = async (): Promise<EmailViewSummary[]> => {
        try {
            const favoriteViews = await mailManager.getDashboardViewsSummary();

            return favoriteViews.map(emailViewSummaryMapper);
        } catch (error) {
            console.error(error);
            notifyFetchError({
                queryKey: queryKeys.dashboardViews(),
                titleTranslationKey: "errors.dashboardSummary",
            });
            throw error;
        }
    };

    const { data: emailViewList = [], isLoading: loading } = useQuery({
        queryKey: queryKeys.dashboardViews(),
        queryFn: fetchDashboardData,
    });

    const handleDashboardViewsUpdated = (favoriteViews: ViewSummary[]) => {
        const mappedViews = favoriteViews.map(emailViewSummaryMapper);
        queryClient.setQueryData(queryKeys.dashboardViews(), mappedViews);
    };

    const { off: offDashboardViewsUpdated } = useGetEventBus<ViewSummary[]>(
        eventKeys.dashboard.favoritesUpdated,
        handleDashboardViewsUpdated,
    );

    useEffect(
        () => () => {
            offDashboardViewsUpdated(
                eventKeys.dashboard.favoritesUpdated,
                handleDashboardViewsUpdated,
            );
        },
        [offDashboardViewsUpdated],
    );

    return { emailViewList, loading };
};

export default useGetDashboardData;
